// src/components/profile/PhoneActions.js
// Rangée d'actions rondes à droite d'un numéro de profil : WhatsApp, appel,
// SMS, copie. Les actions arrivent déjà arbitrées par getMemberPhoneActions
// (confidentialité + contrôle d'accès) : ce composant ne décide de rien, il
// n'affiche que ce qu'on lui donne.
//
// Tactile vs desktop se règle en CSS (`pointer: coarse`), pas en JS : `tel:` et
// `sms:` n'ont de sens que sur un téléphone, alors qu'au bureau « copier » est
// l'action utile. Pas de flash d'hydratation, pas de détection à maintenir.
import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import CIcon from '@coreui/icons-react'
import { cibWhatsapp, cilPhone, cilCommentSquare, cilCopy, cilCheckAlt } from '@coreui/icons'
import { useTranslation } from 'react-i18next'

const COPIED_FEEDBACK_MS = 1800

const PhoneActions = ({ actions }) => {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const timerRef = useRef(null)

  useEffect(() => () => clearTimeout(timerRef.current), [])

  if (!actions) return null
  const { whatsappUrl, telUrl, smsUrl, copyValue } = actions
  if (!whatsappUrl && !telUrl && !smsUrl && !copyValue) return null

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyValue)
      setCopied(true)
      clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS)
    } catch {
      // Presse-papiers indisponible (permissions) : pas critique, le numéro
      // reste affiché et sélectionnable à côté.
    }
  }

  const label = (key) => t(`forms:member_card.${key}`)

  return (
    <span className="phone-actions">
      {whatsappUrl && (
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="phone-action phone-action--whatsapp"
          title={label('whatsapp_title')}
          aria-label={label('whatsapp_title')}
        >
          <CIcon icon={cibWhatsapp} />
        </a>
      )}
      {telUrl && (
        <a
          href={telUrl}
          className="phone-action phone-action--call phone-action--touch"
          title={label('phone_call_title')}
          aria-label={label('phone_call_title')}
        >
          <CIcon icon={cilPhone} />
        </a>
      )}
      {smsUrl && (
        <a
          href={smsUrl}
          className="phone-action phone-action--sms phone-action--touch"
          title={label('phone_sms_title')}
          aria-label={label('phone_sms_title')}
        >
          <CIcon icon={cilCommentSquare} />
        </a>
      )}
      {copyValue && (
        <button
          type="button"
          onClick={handleCopy}
          className={`phone-action phone-action--copy phone-action--fine${
            copied ? ' is-copied' : ''
          }`}
          title={copied ? label('phone_copied') : label('phone_copy_title')}
          aria-label={copied ? label('phone_copied') : label('phone_copy_title')}
        >
          <CIcon icon={copied ? cilCheckAlt : cilCopy} />
        </button>
      )}
    </span>
  )
}

PhoneActions.propTypes = {
  actions: PropTypes.shape({
    whatsappUrl: PropTypes.string,
    telUrl: PropTypes.string,
    smsUrl: PropTypes.string,
    copyValue: PropTypes.string,
  }),
}

export default PhoneActions
